import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { midpointMm, PointMm } from '~/domain/geometry/patientGeometry';
import { LineProfile, ProfileLine } from '~/domain/intensity/lineProfile';
import {
    ProfileComparison,
    ProfileComparisonOptions,
    buildProfileComparison,
} from '~/domain/intensity/lineProfileDatasets';
import { AnnotationGoTo, useAnnotationGoTo } from '~/hooks/useAnnotationGoTo';
import { useImageForSeries } from '~/hooks/image/useImageForSeries';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { dicomProcessorVersionState } from '~/state/atoms/dicomProcessorAtoms';
import { lineProfileListState } from '~/state/atoms/intensityToolAtoms';

export interface LineProfileController extends AnnotationGoTo {
    /**
     * The profile, placed or being placed, sampled across every loaded
     * co-registered dataset, grouped by display unit for overlay comparison,
     * with what the comparison omits.
     */
    comparisonForProfile: (
        profile: ProfileLine,
        options?: ProfileComparisonOptions
    ) => ProfileComparison;
}

/** Where go-to moves the crosshair: the midpoint, which is on the profile's slice. */
const midpointOf = (profile: LineProfile): PointMm =>
    midpointMm(profile.startMm, profile.endMm);

/**
 * Go-to for line profiles, and the comparison of a profile across the loaded
 * datasets. Placing a profile and sampling it is the engine tool's job, not
 * this hook's.
 */
export function useLineProfileController(): LineProfileController {
    const list = useAnnotationGoTo(lineProfileListState, midpointOf);
    const imageForSeries = useImageForSeries();
    const patient = useSelectedPatient();
    // patient.images/doses are mutated in place on import/delete, so subscribe
    // to the processor-version counter (bumped by both paths) to refresh the
    // comparison. Deliberately NOT useDomainVersion(): it fires on every
    // changeset (brush stroke, rename) and each refresh re-samples every
    // co-registered dataset along the line.
    const dicomProcessorVersion = useRecoilValue(dicomProcessorVersionState);

    const comparisonForProfile = useCallback(
        (
            profile: ProfileLine,
            options?: ProfileComparisonOptions
        ): ProfileComparison =>
            buildProfileComparison(
                profile,
                patient?.images ?? [],
                patient?.doses ?? [],
                imageForSeries(profile.plane.seriesInstanceUid),
                options
            ),
        // dicomProcessorVersion is a refresh tripwire, not read in the body.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [patient, imageForSeries, dicomProcessorVersion]
    );

    return { ...list, comparisonForProfile };
}
